import "./Header.css";
import { React, useState, useRef, useEffect } from "react";
import { clearAuthTokens } from "../../api/index";
import { clearUserDetails } from "../../Redux/Slices/userDetailsSlice";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { setActiveComponent } from "../../Redux/Slices/activeComponentSlice";
import BallotProgressPanel from "../BallotProgressPanel/BallotProgressPanel";

function Header() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const voteId = searchParams.get("voteId");
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userDetails = useSelector((state) => state.userDetails.userDetails);
  const [headerImgError, setHeaderImgError] = useState(false);

  useEffect(() => {
    setHeaderImgError(false);
  }, [userDetails?.photos]);

  // Helper function to get user initials
  const getUserInitials = (firstName, lastName) => {
    const firstInitial = firstName ? firstName.charAt(0).toUpperCase() : "";
    const lastInitial = lastName ? lastName.charAt(0).toUpperCase() : "";
    return firstInitial + lastInitial;
  };

  // Helper function to generate background color based on initials
  const getBackgroundColor = (initials) => {
    const colors = [
      "#FF6B6B",
      "#4ECDC4",
      "#45B7D1",
      "#96CEB4",
      "#FFEAA7",
      "#DDA0DD",
      "#98D8C8",
      "#F7DC6F",
      "#BB8FCE",
      "#85C1E9",
      "#F8C471",
      "#82E0AA",
    ];

    let hash = 0;
    for (let i = 0; i < initials.length; i++) {
      hash = initials.charCodeAt(i) + ((hash << 5) - hash);
    }
    hash = Math.abs(hash);
    return colors[hash % colors.length];
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsProfileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleProfileMenu = () => {
    setIsProfileMenuOpen(!isProfileMenuOpen);
  };

  const handleLogout = async () => {
    clearAuthTokens();
    localStorage.removeItem("userId");
    localStorage.removeItem("redirectAfterSignIn");

    dispatch(clearUserDetails());
    setIsProfileMenuOpen(false);
    console.log("Logout successful");
    navigate("/login");
    dispatch(setActiveComponent("getStarted"));
  };

  const handleLogoClick = () => {
    navigate("/ballots");
  };

  const getHeaderText = () => {
    const pathname = location.pathname;
    const pathnameArray = pathname.split("/");
    const pathnameArrayLastSegment = pathnameArray[pathnameArray.length - 1];
    const firstSegment = pathnameArray[1];

    // Handle Certification routes
    if (firstSegment === "certifications") {
      if (pathnameArray.length === 2 || !pathnameArray[2]) {
        return "Certifications";
      }
      if (pathnameArray[3] === "learn") {
        return "Learning Modules";
      }
      if (pathnameArray[3] === "exam") {
        return "Exam";
      }
      if (pathnameArray[3] === "result") {
        return "Exam Result";
      }
      return "Certification Details";
    }

    // Handle Community routes
    if (firstSegment === "communities") {
      if (pathnameArray.length === 2 || !pathnameArray[2]) {
        return "Communities";
      }
      return "Community Space";
    }

    // Handle My Certificates routes
    if (firstSegment === "my-certificates") {
      if (pathnameArray.length === 2 || !pathnameArray[2]) {
        return "My Certificates";
      }
      return "Certificate Details";
    }

    // Handle Payment routes
    if (firstSegment === "payment") {
      const secondSegment = pathnameArray[2];
      if (secondSegment === "success") {
        return "Payment Success";
      }
      if (secondSegment === "cancel") {
        return "Payment Cancelled";
      }
      return "Payment";
    }

    // Handle dedicated Earnings & Wallet routes
    if (
      pathname === "/facilitate/wallet" ||
      pathname === "/facilitate/settings/payouts" ||
      pathname === "/adventure/wallet" ||
      pathname === "/adventure/settings/payouts" ||
      pathname === "/affiliate-earnings"
    ) {
      return "Earnings & Wallet";
    }

    // Handle Facilitate routes
    if (firstSegment === "facilitate" || firstSegment === "adventure") {
      return "Facilitate";
    }

    // Handle Educate / Gear route
    if (pathnameArrayLastSegment === "educate" || pathnameArrayLastSegment === "gear") {
      return "Educate";
    }

    // Handle Help Center route
    if (pathnameArrayLastSegment === "help-center") {
      return "Help Center";
    }

    // Extract the endpoint/route from the pathname
    if (pathnameArrayLastSegment === "ballots") {
      return "Ballots";
    } else if (pathnameArrayLastSegment === "add-ballot" || pathnameArrayLastSegment === "create-ballot") {
      return "Add Ballot";
    } else if (pathnameArrayLastSegment === "profile-details") {
      return "Profile Details";
    } else if (pathnameArrayLastSegment === "edit-cover-photo") {
      return "Edit Cover Photo";
    } else if (pathnameArrayLastSegment === "edit-name") {
      return "Edit Name";
    } else if (pathnameArrayLastSegment === "edit-dob") {
      return "Edit Date of Birth";
    } else if (pathnameArrayLastSegment === "change-password") {
      return "Change Password";
    } else if (pathnameArrayLastSegment === "saved-ballots") {
      return "Saved Ballots";
    } else if (pathnameArrayLastSegment === "ballots-history") {
      return "Ballots History";
    } else if (pathnameArrayLastSegment === "shorts-videos") {
      return "Shorts Video";
    } else if (pathnameArrayLastSegment === "upload-shorts") {
      return "Upload Shorts";
    } else if (pathnameArrayLastSegment === "questionnaire") {
      return "Questionnaire";
    } else if (pathnameArrayLastSegment === "questions") {
      return "Questionnaire";
    } else if (pathnameArrayLastSegment === "questionnaire-role-select") {
      return "Questionnaire";
    } else if (pathnameArrayLastSegment === "edit-language") {
      return "Edit Language";
    } else if (pathnameArrayLastSegment === "edit-address") {
      return "Edit Address";
    } else if (pathnameArrayLastSegment === "edit-gender") {
      return "Edit Gender";
    } else if (pathnameArrayLastSegment === "edit-political-affiliation") {
      return "Edit Political Affiliation";
    } else if (pathnameArrayLastSegment === "edit-professions") {
      return "Edit Professions";
    } else if (pathnameArrayLastSegment === "request-status") {
      return "Request Status";
    } else if (pathnameArrayLastSegment === "vote") {
      return "Ballots";
    } else if (pathnameArrayLastSegment === "shorts-video") {
      return "Shorts Video";
    } else if (pathnameArrayLastSegment === "affiliate-referrals") {
      return "Affiliate-Referrals";
    } else if (pathnameArrayLastSegment === "affiliate-earnings") {
      return "Affiliate-Earnings";
    } else if (pathnameArrayLastSegment === "profile-completeness") {
      return "Complete Profile";
    } else if (pathnameArrayLastSegment === "rewards") {
      return "Rewards";
    } else if (pathnameArrayLastSegment === "my-ballots") {
      return "My Ballots";
    } else {
      return "Not known";
    }
  };

  return (
    <div className="Header">
      <div className="HeaderContainer">
        <div className="HeaderLogoContainer">
          <img
            className="amalgamateHeaderLogo"
            src="/Assets/LandingPage/AmalgamateLogo.svg"
            alt="no img found"
            onClick={handleLogoClick}
          />
        </div>
        <div className="HeaderHeadingContainer">
          <span className="HeaderHeading">{getHeaderText()}</span>
        </div>
        <div className="headerRightActions">
          {location.pathname.includes("/help-center") && (
            <button 
              className="back-to-hub-btn" 
              onClick={() => navigate('/')}
            >
              ← Back to Hub
            </button>
          )}
          <BallotProgressPanel />
          <div
            className="headerProfileIconWrapper"
            onClick={toggleProfileMenu}
            ref={dropdownRef}
          >
          {userDetails?.photos && !headerImgError ? (
            <img
              src={userDetails.photos}
              alt="Profile"
              className="profileImage"
              onError={() => setHeaderImgError(true)}
            />
          ) : (
            <div
              className="profileImage"
              style={{
                backgroundColor: getBackgroundColor(
                  getUserInitials(userDetails?.firstName, userDetails?.lastName)
                ),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                fontWeight: "bold",
                fontSize: "16px",
                fontFamily: "Arial, sans-serif",
                cursor: "pointer",
              }}
            >
              {getUserInitials(userDetails?.firstName, userDetails?.lastName)}
            </div>
          )}
          {isProfileMenuOpen && (
            <div className="profileMenu">
              <div className="profileMenuItem">
                {/* <span className="profileMenuItemText">Profile</span> */}
                <span className="profileMenuItemText" onClick={handleLogout}>
                  Logout
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}

export default Header;
